import parse from 'csv-parse/lib/sync';

import {
  type ComponentKind,
  fillMissingInputNames,
  type PredictionInput,
  makeComponent,
  makeInput,
  validateAllInputs,
} from '../newPredictionState';
import {FileImportError, type FileImporter, type FileImportResult, normalizeMimeType} from './FileImport';

const NAME_HEADER = 'name';
const CSV_MIME_TYPE = 'text/csv';
const TSV_MIME_TYPE = 'text/tab-separated-values';

const COMPONENT_TYPE_HEADERS: Record<string, ComponentKind> = {
  aminoacids: 'aminoAcids',
  smiles: 'smiles',
};

type ComponentColumnTypes = Array<ComponentKind>;
type Delimiter = ',' | '\t';

function parseHeaderRow(headerRow: Array<string>): ComponentColumnTypes {
  const headers = headerRow.map((header) => header.trim());
  if (headers.length === 0 || headers[0].toLowerCase() !== NAME_HEADER) {
    throw new FileImportError('Unexpected columns', `The first column must be "${NAME_HEADER}".`);
  }

  const componentHeaders = headers.slice(1);
  if (componentHeaders.length === 0) {
    throw new FileImportError(
      'Unexpected columns',
      'Add at least one component column (aminoAcids or smiles).'
    );
  }

  return componentHeaders.map((header) => {
    const type = COMPONENT_TYPE_HEADERS[header.toLowerCase()];
    if (type == null) {
      const allowed = Object.keys(COMPONENT_TYPE_HEADERS).join(', ');
      throw new FileImportError(
        'Unexpected columns',
        `Unrecognized component column "${header}". Use one of: ${allowed}.`
      );
    }
    return type;
  });
}

function rowToInput(row: Array<string>, componentTypes: ComponentColumnTypes): PredictionInput | null {
  const components = componentTypes
    .map((type, index) => ({type, value: (row[index + 1] ?? '').trim()}))
    .filter((component) => component.value.length > 0);

  if (components.length === 0) {
    return null;
  }

  return {
    ...makeInput(),
    name: (row[0] ?? '').trim(),
    components: components.map((component) => ({...makeComponent(component.type), value: component.value})),
  };
}

export function parseDelimitedTextInputs(
  text: string,
  delimiter: Delimiter,
  maxInputs: number,
  usedInputNames: ReadonlySet<string> = new Set()
): FileImportResult {
  let rows: Array<Array<string>>;
  try {
    rows = parse(text, {delimiter, trim: true, skip_empty_lines: true, relax_column_count: true});
  } catch {
    throw new FileImportError('Failed to parse spreadsheet', "That file couldn't be read.");
  }

  if (rows.length === 0) {
    throw new FileImportError('No inputs to import', 'The file is empty.');
  }

  const componentTypes = parseHeaderRow(rows[0]);
  const dataRows = rows.slice(1);
  if (dataRows.length === 0) {
    throw new FileImportError('No inputs to import', 'The file has no data rows.');
  }

  const inputs = dataRows
    .map((row) => rowToInput(row, componentTypes))
    .filter((input): input is PredictionInput => input != null);

  if (inputs.length === 0) {
    throw new FileImportError('No inputs to import', 'No rows had any sequence values.');
  }

  if (inputs.length > maxInputs) {
    throw new FileImportError(
      'Too many inputs',
      `This file has ${inputs.length} inputs, but a batch can have at most ${maxInputs}.`
    );
  }

  const validatedInputs = validateAllInputs(fillMissingInputNames(inputs, usedInputNames));

  return {
    inputs: validatedInputs,
    summary: {
      importedComponentCount: validatedInputs.reduce((count, input) => count + input.components.length, 0),
      importedInputCount: validatedInputs.length,
      invalidInputCount: validatedInputs.filter((input) =>
        input.components.some((component) => component.status.kind === 'invalid')
      ).length,
      sourceRecords: {
        count: dataRows.length,
        label: 'row',
        skippedCount: dataRows.length - validatedInputs.length,
      },
    },
  };
}

export function buildInputsCsvTemplate(): string {
  return `${NAME_HEADER},aminoAcids,aminoAcids,aminoAcids,aminoAcids
pd1-pdl1-4zqk,AFTVTVPKDLYVVEYGSNMTIECKFPVEKQLDLAALIVYWEMEDKNIIQFVHGEEDLKVQHSSYRQRARLLKDQLSLGNAALQITDVKLQDAGVYRCMISYGGADYKRITVKVNA,NPPTFSPALLVVTEGDNATFTCSFSNTSESFVLNWYRMSPSNQTDKLAAFPEDRSQPGQDSRFRVTQLPNGRDFHMSVVRARRNDSGTYLCGAISLAPKAQIKESLRAELRVTERRAE,,
barnase-barstar-1brs,AQVINTFDGVADYLQTYHKLPDNYITKSEAQALGWVASKGNLADVAPGKSIGGDIFSNREGKLPGKSGRTWREADINYTSGFRNSDRILYSSDWLIYKTTDHYQTFTKIR,KKAVINGEQIRSISDLHQTLKKELALPEYYGENLDALWDALTGWVEYPLVLEWRQFEQSKQLTENGAESVLQVFREAKAEGADITIILS,,
hyhel10-fab-lysozyme-3hfm,DVQLQESGPSLVKPSQTLSLTCSVTGDSITSDYWSWIRKFPGNRLEYMGYVSYSGSTYYNPSLKSRISITRDTSKNQYYLDLNSVTTEDTATYYCANWDGDYWGQGTLVTVSAAKTTPPSVYPLAPGSAAQTNSMVTLGCLVKGYFPEPVTVTWNSGSLSSGVHTFPAVLQSDLYTLSSSVTVPSSPRPSETVTCNVAHPASSTKVDKKIVPRDC,DIVLTQSPATLSVTPGNSVSLSCRASQSIGNNLHWYQQKSHESPRLLIKYASQSISGIPSRFSGSGSGTDFTLSINSVETEDFGMYFCQQSNSWPYTFGGGTKLEIKRADAAPTVSIFPPSSEQLTSGGASVVCFLNNFYPKDINVKWKIDGSERQNGVLNSWTDQDSKDSTYSMSSTLTLTKDEYERHNSYTCEATHKTSTSPIVKSFNRNEC,KVFGRCELAAAMKRHGLDNYRGYSLGNWVCAAKFESNFNTQATNRNTDGSTDYGILQINSRWWCNDGRTPGSRNLCNIPCSALLSSDITASVNCAKKIVSDGNGMNAWVAWRNRCKGTDVQAWIRGCRL,
hgh-receptor-3hhr,FPTIPLSRLFDNAMLRAHRLHQLAFDTYQEFEEAYIPKEQKYSFLQNPQTSLCFSESIPTPSNREETQQKSNLELLRISLLLIQSWLEPVQFLRSVFANSLVYGASDSNVYDLLKDLEEGIQTLMGRLEDGSPRTGQIFKQTYSKFDTNSHNDDALLKNYGLLYCFRKDMDKVETFLRIVQCRSVEGSCG,EPKFTKCRSPERETFSCHWTDEVHHGTKNLGPIQLFYTRRNTQEWTQEWKECPDYVSAGENSCYFNSSFTSIWIPYCIKLTSNGGTVDEKCFSVDEIVQPDPPIALNWTLLNVSLTGIHADIQVRWEAPRNADIQKGWMVLEYELQYKEVNETKWKMMDPILTTSVPVYSLKVDKEYEVRVRSKQRNSGNYGEFSEVLYVTLPQM,EPKFTKCRSPERETFSCHWTDEVHHGTKNLGPIQLFYTRRNTQEWTQEWKECPDYVSAGENSCYFNSSFTSIWIPYCIKLTSNGGTVDEKCFSVDEIVQPDPPIALNWTLLNVSLTGIHADIQVRWEAPRNADIQKGWMVLEYELQYKEVNETKWKMMDPILTTSVPVYSLKVDKEYEVRVRSKQRNSGNYGEFSEVLYVTLPQM,
il2-quaternary-2b5i,APTSSSTKKTQLQLEHLLLDLQMILNGINNYKNPKLTRMLTFKFYMPKKATELKHLQCLEEELKPLEEVLNLAQSKNFHLRPRDLISNINVIVLELKGSETTFMCEYADETATIVEFLNRWITFCQSIISTLT,AVQGTSQFTCFYNSRAQISCVWSQDGALQDTSCQVHAWPDRRRWQQTCELLPVSQASWACNLILGAPDSQKLTTVDIVTLRVLCREGVRWRVMAIQDFKPFENLRLMAPISLQVVHVETHRCNISWEISQASHYFERHLEFEARTLSPGHTWEEAPLLTLKQKQEWICLETLTPDTQYEFQVRVKPLQGEFTTWSPWSQPLAFRTKPAALGKDT,PLPEVQCFVFNVEYMNCTWQSSSEPQPTNLTLHYWYKNSDNDKVQKCSHYLFSEEITSGCQLQKKEIHLYQTFVVQLQDPREPRRQATQMLKLQNLVIPWAPENLTLHKLSESQLELNWNNRFLNHCLEHLVQYRTDWDHSWTEQSVDYRHKFSLPSVDGQKRYTFRVRSRFNPLCGSAQHWSEWSHPIHWGSNTSKEN,ELCDDDPPEIPHATFKAMAYKEGTMLNCECKRGFRRIKSGSLYMLCTGNSSHSSWDNQCQCTSSATRQTTKQVTPQPEEQKERKTTEMQSPMQPVDQASLPGHCREPPPWENEATERIYHFVVGQMVYYQCVQGYRALHRGPAESVCKMTHGKTRWTQPQLICTGEMETSQFPGEEKPQASPEGRPESETSCLVTTTDFQIQTEMAATMETSIFTTE`;
}

const acceptedFileTypes = {
  [CSV_MIME_TYPE]: ['.csv'],
  [TSV_MIME_TYPE]: ['.tsv'],
};

function getDelimiter(file: File): Delimiter {
  const normalizedFileName = file.name.toLowerCase();
  if (normalizedFileName.endsWith('.tsv')) {
    return '\t';
  }
  if (normalizedFileName.endsWith('.csv')) {
    return ',';
  }
  return normalizeMimeType(file.type) === TSV_MIME_TYPE ? '\t' : ',';
}

export const delimitedTextFileImporter: FileImporter = {
  acceptedFileTypes,
  importFile: async (file, maxInputs, usedInputNames) =>
    parseDelimitedTextInputs(await file.text(), getDelimiter(file), maxInputs, usedInputNames),
};
